import React, { useCallback, useState } from "react";
import { InfoBanner, Spinner, TableHeader } from "@services/ui-components";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { ApolloError, useReactiveVar } from "@apollo/client";
import { currentBranchEmployeeIdVar } from "@me-team/host/main/globalVar/state";
import ProductsToWriteOff from "./ProductsToWriteOff/ProductsToWriteOff";
import {
    useGetProductsByOperationNumberQuery,
    useGetProductsToWriteOffQuery,
    useUpdateWriteOffProductsMutation,
    useWriteOffProductsMutation,
} from "@me-team/host/main/writeOffProducts/graphql/writeOffProducts.hooks";
import ProductList from "./ProductList/ProductList";
import WriteOffBtns from "./WriteOffBtns/WriteOffBtns";
import {
    CombinedProductsInterface,
    LocationWriteOffPageType,
    ProductListInterfaceForWriteOffPage,
    ProductToWriteOffCustomInterface,
    WriteOffSource,
} from "../../../interfaces/interfaces";
import { ProductWriteOffContainerInput, ProductWriteOffInput } from "@me-team/host/main/graphql/types";
import WriteOffService from "../../../services/WriteOffService/WriteOffService";
import { useWriteOffProducts } from "./hooks/useWriteOffProducts";
import { useGraphQLWriteOffValidationHandler } from "./hooks/useGraphQLWriteOffValidationHandler";
import { parseNotEnoughQuantityCategory } from "./utils/parseNotEnoughQuantityCategory";
import { SimpleModal } from "@services/ui-components/src/SimpleModal/SimpleModal";
import { Col, Container, Row } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import useNavigateWithToast from "./hooks/useNavigateWithToast";
import { useWriteOffProductAccess } from "./hooks/useWriteOffProductAccess";

type GraphQLValidationMap = {
    [productId: number]: {
        code: string;
        message: string;
    }[];
};

type WriteOffProductProps = {
    writeOffSource: WriteOffSource;
};

export const WriteOffProduct: React.FC<WriteOffProductProps> = ({ writeOffSource }) => {
    const { companyId, branchId, bookingUuid, operationNumber } = useParams();
    const location = useLocation() as LocationWriteOffPageType | null;
    const { t: translate } = useTranslation();
    const currentBranchEmployeeId = useReactiveVar(currentBranchEmployeeIdVar);
    const isEditMode = !!operationNumber;
    const [showProductList, setShowProductList] = useState(false);
    const [combinedProducts, setCombinedProducts] = useState<CombinedProductsInterface>(null);
    const [writeOffProductsList, setWriteOffProductsList] = useState<ProductToWriteOffCustomInterface[]>([]);
    const { onChangeProductsToWriteOff } = useWriteOffProducts(setWriteOffProductsList, setCombinedProducts, translate);
    const {
        applyGraphQLValidationErrors,
        applyNotEnoughQuantityCategoryError,
        isWriteOffCompletedModalOpen,
        closeWriteOffCompletedModal,
    } = useGraphQLWriteOffValidationHandler(setCombinedProducts, translate);
    const navigateWithToast = useNavigateWithToast({ branchId, bookingUuid, writeOffSource });

    const { data: productsByCurrentBooking, loading: loadingProductsByCurrentBooking } = useGetProductsToWriteOffQuery({
        skip: isEditMode,
        variables: {
            companyId: Number(companyId),
            branchId: Number(branchId),
            bookingUuid: bookingUuid,
        },
        fetchPolicy: "network-only",
        onCompleted: (data) => {
            const techMaps = data.user.company.branches[0].bookingsList[0].technologicalMaps;
            setCombinedProducts((prevState) => {
                return {
                    ...prevState,
                    productFromTechMap: WriteOffService.productWithTechMapInterfaceInitialize(techMaps),
                };
            });
            const productsInput = WriteOffService.productToWriteOffCustomInterfaceInit(techMaps);
            setWriteOffProductsList(productsInput);
        },
    });

    const { data: productsByOperationNumber, loading: loadingProductsByOperationNumber } =
        useGetProductsByOperationNumberQuery({
            skip: !isEditMode,
            variables: {
                companyId: Number(companyId),
                branchId: Number(branchId),
                operationNumber: operationNumber,
            },
            fetchPolicy: "network-only",
            onCompleted: (data) => {
                const productFromTechMaps = data.user.company.branches[0].storageProductRelationHistoryList.filter(
                    (item) => {
                        return !!item.techMap;
                    }
                );

                if (productFromTechMaps && productFromTechMaps.length > 0) {
                    setCombinedProducts((prevState) => {
                        return {
                            ...prevState,
                            productFromTechMap:
                                WriteOffService.productWithTechMapForUpdateInterfaceInitialize(productFromTechMaps),
                        };
                    });
                }

                const addedProductsToWriteOff = data.user.company.branches[0].storageProductRelationHistoryList.filter(
                    (item) => {
                        return !item.techMap;
                    }
                );
                setCombinedProducts((prevState) => {
                    return {
                        ...prevState,
                        addedProductToWriteOff: WriteOffService.addedProductForUpdateInit(addedProductsToWriteOff),
                    };
                });
                setWriteOffProductsList(
                    WriteOffService.productToUpdateWriteOffCustomInterfaceInit(
                        data.user.company.branches[0].storageProductRelationHistoryList
                    )
                );
            },
        });

    const storageProductRelationHistoryList =
        productsByOperationNumber?.user?.company?.branches?.[0]?.storageProductRelationHistoryList ?? [];
    const operationMetadata = storageProductRelationHistoryList?.[0];
    const { canCreateCurrentWriteOff, canEditCurrentOperation, isAccessPending } = useWriteOffProductAccess({
        writeOffSource,
        isEditMode,
        operationMetadata,
    });

    const [writeOffProducts] = useWriteOffProductsMutation();
    const [updateWriteOffProducts] = useUpdateWriteOffProductsMutation();

    const handleWriteOffMutationError = useCallback(
        (error: ApolloError) => {
            const graphQLErrors = error?.graphQLErrors ?? [];

            const gqlValidation = graphQLErrors[0]?.extensions?.validation as GraphQLValidationMap | undefined;
            if (gqlValidation) {
                applyGraphQLValidationErrors(gqlValidation);
            }

            graphQLErrors.forEach((graphQLError, index) => {
                // New format: "storage_product_relation.not_enough_quantity_{productId}_{storageId}_{quantity}".
                const notEnoughQuantity = parseNotEnoughQuantityCategory(
                    graphQLError?.extensions?.category as string | undefined
                );
                if (notEnoughQuantity) {
                    const isHandled = applyNotEnoughQuantityCategoryError({
                        productId: notEnoughQuantity.productId,
                        storageId: notEnoughQuantity.storageId,
                        writeOffProductsList,
                    });
                    if (isHandled) return;
                }

                if (index === 0 && gqlValidation) return;
            });
        },
        [applyGraphQLValidationErrors, applyNotEnoughQuantityCategoryError, writeOffProductsList]
    );

    const onAddProductToWriteOff = useCallback(
        (product: ProductListInterfaceForWriteOffPage) => {
            setCombinedProducts((prevState) => {
                const prevAdded = prevState?.addedProductToWriteOff ?? [];
                return {
                    ...prevState,
                    addedProductToWriteOff: [
                        ...prevAdded,
                        {
                            ...product,
                            hasError: false,
                            errors: {
                                storageError: null,
                                qtyError: null,
                            },
                            isZeroAccept: false,
                        },
                    ],
                };
            });

            const productInput: ProductWriteOffInput = {
                product: product.id,
                storage: product.storage?.id ?? null,
                quantity: null,
                unit: product.unit.id,
                isZeroAccept: false,
            };

            setWriteOffProductsList((prevState) => [...prevState, productInput]);
        },
        [setCombinedProducts, setWriteOffProductsList]
    );

    const onRemoveProductFromWriteOff = useCallback(
        (productId: number, isProductFromTechMap: boolean, techMapId?: number) => {
            if (isProductFromTechMap) {
                setCombinedProducts((prevState) => {
                    return {
                        ...prevState,
                        productFromTechMap: prevState.productFromTechMap.map((techMap) => {
                            if (techMap.techMapId === techMapId) {
                                return {
                                    ...techMap,
                                    techMapProductInfo: techMap.techMapProductInfo.filter(
                                        (relation) => relation.product.id !== productId
                                    ),
                                };
                            }

                            return techMap;
                        }),
                    };
                });
            } else {
                setCombinedProducts((prevState) => {
                    return {
                        ...prevState,
                        addedProductToWriteOff: prevState.addedProductToWriteOff.filter(
                            (product) => product.id !== productId
                        ),
                    };
                });
            }

            setWriteOffProductsList((prevState) => {
                if (isProductFromTechMap && techMapId !== undefined) {
                    return prevState.filter(
                        (productItem) => productItem.product !== productId || productItem.techMapId !== techMapId
                    );
                }

                return prevState.filter((productItem) => productItem.product !== productId);
            });
        },
        []
    );

    const onSubmitWriteOffProduct = useCallback(async () => {
        if (!!writeOffProductsList.length) {
            const updatedProductFromTechMap = WriteOffService.productFromTechMapValidation(
                combinedProducts.productFromTechMap,
                writeOffProductsList,
                translate
            );
            const updatedAddedProductToWriteOff = WriteOffService.addedProductValidation(
                combinedProducts.addedProductToWriteOff,
                writeOffProductsList,
                translate
            );

            const hasErrorsInTechMap = updatedProductFromTechMap.some((techMap) =>
                techMap.techMapProductInfo.some((product) => product.hasError)
            );

            const hasErrorsInAddedProducts = updatedAddedProductToWriteOff.some((product) => product.hasError);
            const hasAnyErrors = hasErrorsInTechMap || hasErrorsInAddedProducts;

            if (hasAnyErrors) {
                setCombinedProducts(() => {
                    return {
                        productFromTechMap: updatedProductFromTechMap,
                        addedProductToWriteOff: updatedAddedProductToWriteOff,
                    };
                });
            } else {
                if (!isEditMode) {
                    if (!canCreateCurrentWriteOff) return;

                    const writeOffProductsInput: ProductWriteOffContainerInput = {
                        branch: Number(branchId),
                        bookingUuid: bookingUuid,
                        items: WriteOffService.productInputInit(writeOffProductsList),
                    };

                    await writeOffProducts({
                        variables: {
                            branchId: Number(branchId),
                            bookingUuid: bookingUuid,
                            input: writeOffProductsInput,
                        },
                        context: { errorType: "local" },
                        onCompleted: () => {
                            navigateWithToast(translate("Written off"));
                        },
                        onError: handleWriteOffMutationError,
                    });
                } else {
                    if (!canEditCurrentOperation || !currentBranchEmployeeId) return;

                    const updateWriteOffProductsInput: ProductWriteOffContainerInput = {
                        branch: Number(branchId),
                        bookingUuid: bookingUuid,
                        items: WriteOffService.updateProductInputInit(writeOffProductsList),
                        operationNumber: operationNumber,
                    };
                    await updateWriteOffProducts({
                        variables: {
                            branchId: Number(branchId),
                            bookingUuid: bookingUuid,
                            input: updateWriteOffProductsInput,
                            operationNumber: operationNumber,
                            employeeId: Number(currentBranchEmployeeId),
                        },
                        context: { errorType: "local" },
                        onCompleted: () => {
                            navigateWithToast(translate("Saved"));
                        },
                        onError: handleWriteOffMutationError,
                    });
                }
            }
        }
    }, [
        combinedProducts,
        writeOffProductsList,
        translate,
        isEditMode,
        operationNumber,
        branchId,
        bookingUuid,
        canCreateCurrentWriteOff,
        canEditCurrentOperation,
        currentBranchEmployeeId,
        writeOffProducts,
        updateWriteOffProducts,
        navigateWithToast,
        handleWriteOffMutationError,
    ]);

    const isDataPending = isEditMode
        ? !productsByOperationNumber || loadingProductsByOperationNumber
        : !productsByCurrentBooking || loadingProductsByCurrentBooking;
    const isSubmitWriteOffProductDisabled = isEditMode ? !canEditCurrentOperation : !canCreateCurrentWriteOff;

    if (isDataPending || isAccessPending) return <Spinner />;

    if (!isEditMode && !canCreateCurrentWriteOff) return <Navigate to="/error/403" replace />;

    return (
        <>
            <InfoBanner>
                <p className="fs-7 mb-0">
                    {translate("Write off the products that were used while providing the service to the client.")}
                </p>
            </InfoBanner>

            <TableHeader
                title={translate(isEditMode ? "Edit Products Write-Off" : "Write-off of Products")}
                paths={WriteOffService.breadcrumbsFromPage(location, branchId, translate, writeOffSource)}
            />

            {combinedProducts && (
                <ProductsToWriteOff
                    changeProductsToWriteOff={onChangeProductsToWriteOff}
                    combinedProducts={combinedProducts}
                    removeProductFromWriteOff={onRemoveProductFromWriteOff}
                />
            )}
            <WriteOffBtns
                isProductNotEmpty={!!writeOffProductsList.length}
                isShowProductList={showProductList}
                onToggleShowProductList={() => setShowProductList((prevState) => !prevState)}
                submitWriteOffBtn={onSubmitWriteOffProduct}
                isEditMode={isEditMode}
                isSubmitDisabled={isSubmitWriteOffProductDisabled}
            />
            {showProductList && (
                <ProductList combinedProducts={combinedProducts} addProductToWriteOff={onAddProductToWriteOff} />
            )}
            <SimpleModal
                size={"lg"}
                isOpen={isWriteOffCompletedModalOpen}
                onClose={closeWriteOffCompletedModal}
                title={translate("Please note")}
                description={translate(
                    "The consumables for this visit have already been written off. Repeated write-off is not possible. You can go to the list of warehouse operations and edit the existing one."
                )}
                children={
                    <Container>
                        <Row className="justify-content-end">
                            <Col xs={12} md={6} lg={3} xl={3} className="px-0 mb-2 mb-md-0 px-md-1">
                                <Button
                                    as="a"
                                    href={`/logbook/journal/${branchId}`}
                                    variant="outline-primary"
                                    className="w-100 fw-normal"
                                >
                                    {translate("To Logbook Write Off")}
                                </Button>
                            </Col>
                            <Col xs={12} md={6} lg={3} xl={3} className="px-0 ps-md-1 pe-md-0">
                                <Button
                                    as="a"
                                    href={`/warehouses/operations/${branchId}`}
                                    variant="primary"
                                    className="w-100 fw-normal"
                                >
                                    {translate("To Warehouse Operations")}
                                </Button>
                            </Col>
                        </Row>
                    </Container>
                }
            />
        </>
    );
};
